import React, { useState } from 'react';
import { XIcon, AlertCircle, CheckCircle } from 'lucide-react';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { TermsModal } from './TermsModal';
interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}
// Helper function to parse and format error messages
const getErrorMessage = (data: any, response: Response): string => {
  // Check for API response with CodigoRespuesta and DescripcionRespuesta
  if (data?.CodigoRespuesta || data?.DescripcionRespuesta) {
    const codigo = data.CodigoRespuesta;
    const descripcion = data.DescripcionRespuesta || '';
    // CodigoRespuesta "000" typically means success, anything else is an error
    if (codigo !== '000') {
      const lowerDesc = descripcion.toLowerCase();
      // Check for duplicate/already registered
      if (lowerDesc.includes('ya fue registrado') || lowerDesc.includes('ya registrado') || lowerDesc.includes('ya existe')) {
        return 'Este usuario ya está registrado en el sistema. Si necesitas ayuda, contacta con soporte.';
      }
      // Return the API description if available
      if (descripcion) {
        return descripcion;
      }
      // Fallback based on error code
      return `Error ${codigo}: No se pudo completar el registro.`;
    }
  }
  // Check for other common error message fields
  const apiMessage = data?.message || data?.error || data?.mensaje || data?.descripcion;
  if (apiMessage) {
    const lowerMessage = apiMessage.toLowerCase();
    // Check for duplicate/already registered errors
    if (lowerMessage.includes('ya existe') || lowerMessage.includes('ya registrado') || lowerMessage.includes('duplicado') || lowerMessage.includes('already exists') || lowerMessage.includes('duplicate')) {
      return 'Este usuario ya está registrado. Si olvidaste tu contraseña, contacta con soporte.';
    }
    // Check for invalid data errors
    if (lowerMessage.includes('inválido') || lowerMessage.includes('invalid') || lowerMessage.includes('formato incorrecto')) {
      return `Datos inválidos: ${apiMessage}`;
    }
    // Check for missing required fields
    if (lowerMessage.includes('requerido') || lowerMessage.includes('required') || lowerMessage.includes('obligatorio')) {
      return `Campo requerido: ${apiMessage}`;
    }
    // Return the original API message if it's user-friendly
    if (apiMessage.length < 150) {
      return apiMessage;
    }
  }
  // HTTP status-based messages
  if (response.status === 400) {
    return 'Los datos enviados son incorrectos. Por favor verifica la información.';
  }
  if (response.status === 409) {
    return 'Este usuario ya está registrado en el sistema.';
  }
  if (response.status === 500) {
    return 'Error en el servidor. Por favor intenta nuevamente en unos minutos.';
  }
  if (response.status === 503) {
    return 'El servicio no está disponible temporalmente. Intenta más tarde.';
  }
  // Default error message
  return 'Hubo un error al procesar tu registro. Por favor intenta nuevamente o contacta con soporte.';
};
export function SubscriptionModal({
  isOpen,
  onClose
}: SubscriptionModalProps) {
  const [formData, setFormData] = useState({
    documentNumber: '',
    lastName: '',
    firstName: '',
    phone: '',
    email: '',
    isAdult: false,
    acceptTerms: false
  });
  const [documentType, setDocumentType] = useState('CEDULA');
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  if (!isOpen) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowError(false);
    try {
      const response = await fetch('https://admin.benefika.net/webservice/comprobante/api/RegistroUsuarioConvenio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authentication: 'f23f90bc750e241604ce3afbda15226b090808a0e77a5237f7e8713883a5c460'
        },
        body: JSON.stringify({
          TipoIdentificacion: documentType,
          Cedula: formData.documentNumber,
          Nombre: formData.firstName,
          Apellido: formData.lastName,
          Email: formData.email,
          Telefono: formData.phone
        })
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Error al procesar la respuesta del servidor.');
      }
      // Log the response for debugging
      console.log('API Response:', {
        status: response.status,
        data
      });
      // Check for errors based on CodigoRespuesta
      // "000" = success, anything else = error
      const isSuccess = data.CodigoRespuesta === '000';
      if (!isSuccess) {
        const errorMsg = getErrorMessage(data, response);
        throw new Error(errorMsg);
      }
      // Additional fallback checks for other error formats
      if (!response.ok || data.error || data.status === 'error' || data.success === false) {
        const errorMsg = getErrorMessage(data, response);
        throw new Error(errorMsg);
      }
      // Show success message
      setShowSuccess(true);
      // Reset form
      setFormData({
        documentNumber: '',
        lastName: '',
        firstName: '',
        phone: '',
        email: '',
        isAdult: false,
        acceptTerms: false
      });
      setDocumentType('CEDULA');
      // Auto close after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error al registrar:', error);
      const message = error instanceof Error ? error.message : 'Hubo un error al procesar tu registro. Por favor intenta nuevamente.';
      setErrorMessage(message);
      setShowError(true);
      // Auto hide error after 8 seconds
      setTimeout(() => {
        setShowError(false);
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleDocumentTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentType(e.target.value);
  };
  return <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-id="element-225">
        <div className="relative w-full max-w-md bg-gray-900 border-2 border-yellow-600/30 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" data-id="element-226">
          {/* Error Message */}
          {showError && <div className="absolute top-0 left-0 right-0 z-40 p-4" data-id="element-227">
              <div className="bg-red-500/90 backdrop-blur-lg border border-red-400 rounded-lg p-4 flex items-start gap-3 shadow-lg" data-id="element-228">
                <AlertCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" data-id="element-229" />
                <div className="flex-1" data-id="element-230">
                  <h4 className="text-white font-bold mb-1" data-id="element-231">
                    Error en el registro
                  </h4>
                  <p className="text-white/90 text-sm leading-relaxed" data-id="element-232">
                    {errorMessage}
                  </p>
                </div>
                <button onClick={() => setShowError(false)} className="text-white/80 hover:text-white transition-colors" data-id="element-233">
                  <XIcon className="w-4 h-4" data-id="element-234" />
                </button>
              </div>
            </div>}

          {/* Success Message */}
          {showSuccess && <div className="absolute inset-0 bg-gray-900/95 backdrop-blur-lg z-50 flex flex-col items-center justify-center p-8" data-id="element-235">
              <div className="transform scale-0 animate-[scale-in_0.3s_ease-out_forwards]" data-id="element-236">
                <CheckCircle className="w-24 h-24 text-yellow-400 mb-6" data-id="element-237" />
              </div>

              <h3 className="text-3xl font-bold text-white mb-3 text-center opacity-0 animate-[fade-in_0.3s_ease-out_0.3s_forwards]" data-id="element-238">
                ¡Registro Exitoso!
              </h3>

              <p className="text-xl text-yellow-400 text-center opacity-0 animate-[fade-in_0.3s_ease-out_0.4s_forwards]" data-id="element-239">
                Bienvenido a SamboCard
              </p>

              <div className="mt-8 opacity-0 animate-[fade-in_0.3s_ease-out_0.5s_forwards]" data-id="element-240">
                <div className="flex gap-2" data-id="element-241">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" data-id="element-242"></div>
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{
                animationDelay: '0.2s'
              }} data-id="element-243"></div>
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{
                animationDelay: '0.4s'
              }} data-id="element-244"></div>
                </div>
              </div>
            </div>}

          {/* Close button */}
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 transition-colors z-10" data-id="element-245">
            <XIcon className="w-5 h-5 text-gray-400" data-id="element-246" />
          </button>

          {/* Header */}
          <div className="p-6 border-b border-yellow-600/20" data-id="element-247">
            <h2 className="text-2xl font-bold text-white mb-2" data-id="element-248">
              Obtener Mi{' '}
              <span className="bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent" data-id="element-249">
                SamboCard
              </span>
            </h2>
            <p className="text-gray-400 text-sm" data-id="element-250">
              Completa el formulario para recibir tu tarjeta digital
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-251">
            {/* Document Type */}
            <div data-id="element-252">
              <label className="block text-sm font-semibold text-white mb-3" data-id="element-253">
                Tipo de Documento
              </label>
              <div className="flex gap-4" data-id="element-254">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-255">
                  <input type="radio" name="documentType" value="CEDULA" checked={documentType === 'CEDULA'} onChange={handleDocumentTypeChange} className="w-4 h-4 text-yellow-600 bg-gray-800 border-gray-600 focus:ring-yellow-600 focus:ring-2" data-id="element-256" />
                  <span className="text-gray-300" data-id="element-257">Cédula</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-258">
                  <input type="radio" name="documentType" value="PASAPORTE" checked={documentType === 'PASAPORTE'} onChange={handleDocumentTypeChange} className="w-4 h-4 text-yellow-600 bg-gray-800 border-gray-600 focus:ring-yellow-600 focus:ring-2" data-id="element-259" />
                  <span className="text-gray-300" data-id="element-260">Pasaporte</span>
                </label>
              </div>
            </div>

            {/* Document Number */}
            <div data-id="element-261">
              <label htmlFor="documentNumber" className="block text-sm font-semibold text-white mb-2" data-id="element-262">
                {documentType === 'CEDULA' ? 'Cédula' : 'Pasaporte'}
              </label>
              <input type="text" id="documentNumber" name="documentNumber" value={formData.documentNumber} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600" placeholder={documentType === 'CEDULA' ? 'Ingresa tu cédula' : 'Ingresa tu pasaporte'} data-id="element-263" />
            </div>

            {/* Last Name */}
            <div data-id="element-264">
              <label htmlFor="lastName" className="block text-sm font-semibold text-white mb-2" data-id="element-265">
                Apellido/s
              </label>
              <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600" placeholder="Ingresa tus apellidos" data-id="element-266" />
            </div>

            {/* First Name */}
            <div data-id="element-267">
              <label htmlFor="firstName" className="block text-sm font-semibold text-white mb-2" data-id="element-268">
                Nombre/s
              </label>
              <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600" placeholder="Ingresa tus nombres" data-id="element-269" />
            </div>

            {/* Phone */}
            <div data-id="element-270">
              <label htmlFor="phone" className="block text-sm font-semibold text-white mb-2" data-id="element-271">
                Teléfono
              </label>
              <div className="flex gap-2" data-id="element-272">
                <div className="w-20 px-3 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 text-sm flex items-center justify-center" data-id="element-273">
                  +593
                </div>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600" placeholder="999999999" data-id="element-274" />
              </div>
            </div>

            {/* Email */}
            <div data-id="element-275">
              <label htmlFor="email" className="block text-sm font-semibold text-white mb-2" data-id="element-276">
                Email
              </label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600" placeholder="tu@email.com" data-id="element-277" />
            </div>

            {/* Age Confirmation Checkbox */}
            <div data-id="element-278">
              <label className="flex items-start gap-3 cursor-pointer" data-id="element-279">
                <input type="checkbox" name="isAdult" checked={formData.isAdult} onChange={handleChange} required className="mt-1 w-4 h-4 text-yellow-600 bg-gray-800 border-gray-600 rounded focus:ring-yellow-600 focus:ring-2" data-id="element-280" />
                <span className="text-sm text-gray-300" data-id="element-281">
                  Soy mayor de 18 años.
                </span>
              </label>
            </div>

            {/* Terms Checkbox */}
            <div data-id="element-282">
              <label className="flex items-start gap-3 cursor-pointer" data-id="element-283">
                <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} required className="mt-1 w-4 h-4 text-yellow-600 bg-gray-800 border-gray-600 rounded focus:ring-yellow-600 focus:ring-2" data-id="element-284" />
                <span className="text-sm text-gray-300" data-id="element-285">
                  Acepto las{' '}
                  <button type="button" onClick={() => setIsPrivacyModalOpen(true)} className="text-yellow-400 hover:text-yellow-300 underline" data-id="element-286">
                    Políticas de Privacidad y Protección de Datos
                  </button>{' '}
                  y los{' '}
                  <button type="button" onClick={() => setIsTermsModalOpen(true)} className="text-yellow-400 hover:text-yellow-300 underline" data-id="element-287">
                    Términos y Condiciones
                  </button>
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button type="submit" disabled={!formData.acceptTerms || !formData.isAdult || isSubmitting} className="w-full px-6 py-4 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold text-lg rounded-lg hover:from-yellow-500 hover:to-yellow-400 transition-all transform hover:scale-105 shadow-lg shadow-yellow-600/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none" data-id="element-288">
              {isSubmitting ? 'Procesando...' : 'Suscribirme'}
            </button>
          </form>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal isOpen={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} data-id="element-289" />

      {/* Terms Modal */}
      <TermsModal isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} data-id="element-290" />
    </>;
}